import { zeroAddress } from 'viem'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import type { InverseMarketsRaw } from './types'

/**
 * Synthesized per-market lender key, e.g.
 * `INVERSE_63DF5E23DB45A2066508318F172BA45B9CD37035` (= the WETH
 * market). Address-suffixed (Teller/Exactly convention) — FiRM is
 * Ethereum-only so the chain id is not part of the key.
 */
export function inverseLenderKey(lender: string, market: string): string {
  return `${lender}_${market.replace(/^0x/i, '').toUpperCase()}`
}

/** Recover `{ lender, market }` from a per-market key (or undefined). */
export function inverseKeyParts(
  key: string,
): { lender: string; market: string } | undefined {
  if (!key.startsWith('INVERSE_')) return undefined
  const suffix = key.slice('INVERSE_'.length)
  if (!/^[0-9A-F]{40}$/.test(suffix)) return undefined
  return { lender: 'INVERSE', market: '0x' + suffix.toLowerCase() }
}

function currencyFor(
  address: string,
  decimals: number,
  symbol: string,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return tokens[lower] ?? { address: lower, symbol, name: symbol, decimals }
}

/**
 * Map the FiRM batch into the shared `MorphoGeneralPublicResponse`
 * shape, keyed by `INVERSE_<MARKET_ADDR>` — one key per Market
 * (collateral).
 *
 * Per market:
 *  - COLLATERAL entry: deposit-only; LTV = `collateralFactorBps`;
 *    liquidation penalty = `liquidationIncentiveBps`; the close factor
 *    is `liquidationFactorBps` (a liquidation may only close that
 *    share of the position).
 *  - LOAN entry (DOLA): `totalDebt` = market debt; the borrow rate is
 *    the DBR price (FIXED APR — interest is prepaid in DBR, not
 *    accrued on principal, `rateModel: 'dbr'`); `borrowLiquidity` =
 *    `leftToBorrow` (API: min(dailyLimit headroom, DOLA in market)) or
 *    `dolaLiquidity` in the on-chain fallback. There is NO supply side
 *    — `totalDeposits` on the loan row is always 0 (DOLA is Fed-minted
 *    into markets, not user-deposited). When the DBR price is unknown
 *    the loan row is served un-borrowable rather than at 0% — see
 *    `rateKnown` below.
 *
 * The full FiRM descriptor (minDebt, dailyLimit, escrow implementation,
 * DBR addresses, replenishment penalty) rides in
 * `params.market.inverse` for the calldata builders + worker-api
 * resolvers.
 */
export function convertInverseMarketsToResponse(
  raw: InverseMarketsRaw,
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  const cfg = raw?.config
  const chainData = raw?.chainData
  if (!cfg || !chainData) return out

  const dolaAddr = cfg.dola.toLowerCase()
  const debtDecimals = cfg.debtDecimals ?? 18
  const dolaToken = tokens[dolaAddr]
  const dolaPriceKey =
    toOracleKey(dolaToken?.assetGroup!) || toGenericPriceKey(dolaAddr, chainId)
  const dolaPrice = prices[dolaPriceKey] || 1
  // The DBR price is FiRM's ONLY borrow cost, so an unknown price is an
  // unpriced loan — and this is the one lender where a missing rate reads
  // as `0`, i.e. free money, and sorts to the top of every rate ranking.
  // Reaching this state means BOTH sources are gone (the live `/api/dbr`
  // AND the metadata snapshot), so the DOLA side is served UN-BORROWABLE
  // rather than free. The collateral row and the position stay visible.
  const rateKnown = raw.dbrPriceDola !== null && raw.dbrPriceDola > 0
  const borrowAprPct = rateKnown ? raw.dbrPriceDola! * 100 : 0

  for (const m of raw.markets ?? []) {
    const market = m.market
    const lenderKey = inverseLenderKey(raw.lender, market.address)
    const collAddr = market.collToken.toLowerCase()
    const collDecimals = market.collDecimals
    const collToken = tokens[collAddr]
    const collSymbol = collToken?.symbol ?? market.name ?? 'COLL'
    const marketName = `DOLA / ${market.name ?? collSymbol}`

    const collPriceKey =
      toOracleKey(collToken?.assetGroup!) ||
      toGenericPriceKey(collAddr, chainId)
    const collPrice = m.price ?? prices[collPriceKey] ?? 0

    const totalDebt = m.totalDebt ?? 0
    const paused = m.borrowPaused ?? !!market.borrowPaused
    // Nobody may be shown a borrow offer when the protocol has paused the
    // market OR when we cannot price it. `inverse.borrowPaused` keeps
    // reporting the PROTOCOL's own state so the two stay distinguishable.
    const borrowClosed = paused || !rateKnown
    const ltv = Number(market.collateralFactorBps) / 10000
    const liqPenalty = Number(market.liquidationIncentiveBps) / 10000
    // A single liquidation may close at most this share of the debt.
    const closeFactor = Number(market.liquidationFactorBps) / 10000 || 1

    // Collateral in the market is not read directly (it sits across
    // per-user escrows); expose the debt-implied lower bound only when
    // the price is known, else 0. The market-level number the UI needs
    // is the DOLA side anyway.
    const collateralTotal = 0

    let borrowLiquidity = m.leftToBorrow ?? m.dolaLiquidity ?? 0
    if (borrowClosed) borrowLiquidity = 0

    // @ts-ignore — built incrementally, matches the River/Teller converters
    const entry: MorphoGeneralPublicResponse = { data: {} }

    // 1. COLLATERAL entry — deposit-only.
    const collUid = createMarketUid(chainId, lenderKey, collAddr)
    entry.data[collUid] = {
      marketUid: collUid,
      name: 'Collateral ' + collSymbol,
      poolId: market.address.toLowerCase(),
      underlying: collAddr,
      asset: currencyFor(collAddr, collDecimals, collSymbol, tokens),
      totalDeposits: collateralTotal,
      totalDebtStable: 0,
      totalDebt: 0,
      totalLiquidity: collateralTotal,
      borrowLiquidity: 0,
      totalLiquidityUSD: collateralTotal * collPrice,
      borrowLiquidityUSD: 0,
      totalDepositsUSD: collateralTotal * collPrice,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      utilization: 0,
      depositRate: 0,
      variableBorrowRate: 0,
      stableBorrowRate: 0,
      intrinsicYield:
        _additionalYields?.intrinsicYields?.[collToken?.assetGroup!] ?? 0,
      rewards: undefined,
      decimals: collDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: ltv,
          collateralFactor: ltv,
          borrowFactor: 1,
          liquidationPenalty: liqPenalty,
          closeFactor,
          collateralDisabled: false,
          debtDisabled: true,
        },
      },
      closeFactor,
      collateralActive: true,
      borrowingEnabled: false,
      depositsEnabled: true,
      hasStable: false,
      isActive: true,
      isFrozen: false,
    } as any

    // 2. LOAN entry — DOLA debt. No supply side.
    const loanUid = createMarketUid(chainId, lenderKey, dolaAddr)
    entry.data[loanUid] = {
      marketUid: loanUid,
      name: 'DOLA',
      poolId: market.address.toLowerCase(),
      underlying: dolaAddr,
      asset: currencyFor(dolaAddr, debtDecimals, 'DOLA', tokens),
      totalDeposits: 0,
      totalDebtStable: 0,
      totalDebt,
      totalLiquidity: borrowLiquidity,
      borrowLiquidity,
      totalLiquidityUSD: borrowLiquidity * dolaPrice,
      borrowLiquidityUSD: borrowLiquidity * dolaPrice,
      totalDepositsUSD: 0,
      totalDebtStableUSD: 0,
      totalDebtUSD: totalDebt * dolaPrice,
      utilization: 0,
      depositRate: 0,
      // The DBR price IS the fixed borrow APR (percent). Debt principal
      // is static — interest burns the user's DBR balance instead.
      variableBorrowRate: borrowAprPct,
      stableBorrowRate: 0,
      rateModel: 'dbr',
      intrinsicYield: 0,
      rewards: undefined,
      decimals: debtDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          borrowFactor: 1,
          liquidationPenalty: liqPenalty,
          closeFactor,
          collateralDisabled: true,
          debtDisabled: borrowClosed,
        },
      },
      closeFactor,
      collateralActive: false,
      borrowingEnabled: !borrowClosed,
      depositsEnabled: false,
      hasStable: false,
      variableBorrowDisabled: borrowClosed,
      isActive: true,
      isFrozen: false,
    } as any

    entry.params = {
      market: {
        lender: lenderKey,
        name: marketName,
        loanDecimals: debtDecimals,
        collateralDecimals: collDecimals,
        // The Market contract doubles as the market id.
        id: market.address.toLowerCase(),
        lltv: String(ltv),
        oracle: cfg.oracle,
        irm: zeroAddress,
        collateralAddress: collAddr,
        loanAddress: dolaAddr,
        // --- FiRM descriptor (metadata + live snapshot; consumed by
        // calldata builders + worker-api resolvers) ---
        inverse: {
          escrowImplementation: market.escrowImplementation,
          /** DOLA units, human numbers as strings. */
          minDebt: market.minDebt,
          dailyLimit: market.dailyLimit,
          dailyBorrows:
            m.dailyBorrows !== null ? String(m.dailyBorrows) : undefined,
          dolaLiquidity:
            m.dolaLiquidity !== null ? String(m.dolaLiquidity) : undefined,
          leftToBorrow:
            m.leftToBorrow !== null ? String(m.leftToBorrow) : undefined,
          liquidationFactorBps: market.liquidationFactorBps,
          /** Fixed borrow APR as a DECIMAL (DBR price in DOLA). ABSENT when
           *  unknown — never `"0"`, which a consumer reads as a free loan. */
          dbrPriceDola: rateKnown ? String(raw.dbrPriceDola) : undefined,
          /** Force-replenish penalty (5475 = 54.75% APR on missed DBR):
           *  the DOLA debt added per 1 DBR minted back to the borrower. */
          replenishmentPriceBps:
            raw.replenishmentPriceBps !== null
              ? String(raw.replenishmentPriceBps)
              : undefined,
          /** Replenisher bot's cut (1000 = 10%) — carved OUT of the cost
           *  above and paid from market liquidity, NOT charged on top of
           *  the borrower's debt. Bot-side economics (liquidation dashboard). */
          replenishmentIncentiveBps:
            m.replenishmentIncentiveBps !== null
              ? String(m.replenishmentIncentiveBps)
              : undefined,
          borrowPaused: paused,
          addresses: {
            market: market.address,
            dola: cfg.dola,
            dbr: cfg.dbr,
            oracle: cfg.oracle,
            borrowController: cfg.borrowController,
          },
        },
      } as any,
    }
    entry.chainId = chainId

    out[lenderKey] = entry
  }

  return out
}
